<script lang="ts">
	import { onMount } from 'svelte';
	import { t } from 'svelte-i18n';
	import type { ButtonBoxType } from '$lib/elements/buttons/types';
	import ButtonBoxPrimary from '$lib/elements/buttons/variants/ButtonBoxPrimary.svelte';
	import Form from '$lib/elements/Form.svelte';
	import { attrValue } from '$lib/utils/html';
	import { env } from '$env/dynamic/public';

	export let submitButtonProps: ButtonBoxType | undefined = undefined;
	export let hasToTriggerSubmit = false;
	export let submitCallback: (token?: string) => void;
	export let classNames: string;

	let captchaDiv: HTMLElement;
	let widgetId: null | number = null;
	let captchaError: string | undefined;

	const onCaptchaSubmit = (token: string) => {
		captchaError = undefined;
		if (token) {
			submitCallback(token);
			window.grecaptcha && window.grecaptcha?.reset();
		}
	};

	onMount(() => {
		window.onloadCallback = function () {
			widgetId = grecaptcha.render(captchaDiv, {
				sitekey: env.PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY,
				size: 'invisible',
				callback: (token: string) => onCaptchaSubmit(token),
				'expired-callback': () => onCaptchaExpired(),
				'error-callback': () => onCaptchaError()
			});
		};
	});

	const onCaptchaError = () => {
		captchaError = $t('forms.validators.captcha.error');
	};

	const onCaptchaExpired = () => {
		captchaError = $t('forms.validators.captcha.expired');
	};

	$: if (hasToTriggerSubmit) {
		window.grecaptcha && window.grecaptcha?.execute(widgetId);
	}
</script>

<svelte:head>
	<script
		src="https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit"
		async
		defer
	></script>
</svelte:head>

<Form classNames={attrValue([classNames])}>
	<slot />
	{#if captchaError}
		<p class="text-danger">{captchaError}</p>
	{/if}
	<div class="flex justify-center">
		<div bind:this={captchaDiv} />
		{#if submitButtonProps}
			<ButtonBoxPrimary
				props={submitButtonProps}
				on:click={() => {
					window.grecaptcha && window.grecaptcha?.execute(widgetId);
				}}
			/>
		{/if}
	</div>
</Form>

<style lang="postcss">
	:global(.signInForm .grecaptcha-badge, .signUpForm .grecaptcha-badge) {
		position: absolute !important;
		bottom: 14px !important;
		right: -218px !important;
		width: 256px;
		visibility: visible !important;
	}
	:global(.grecaptcha-badge:hover, .grecaptcha-badge:hover) {
		right: -33px !important;
		@apply z-preloading-spinner;
	}

	:global(.modalContent) {
		position: relative !important;
	}
	:global(.grecaptcha-badge) {
		height: 60px !important;
		display: block !important;
		transition: right 0.3s ease 0s !important;
		position: fixed !important;
		bottom: 14px !important;
		right: -186px !important;
		box-shadow: gray 0px 0px 5px !important;
		border-radius: 2px !important;
		overflow: hidden !important;
		visibility: visible !important;
	}
</style>
